import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";

/**
 * How many of today's outbound invite texts are queued and due but not yet
 * sent — the only case where the Invitation tab may say invites are "held"
 * (queued behind the emergency stop). Same filters as send.ts's kill-switch
 * held count (outbound, queued, not_before ≤ now), narrowed to today's invite
 * campaign so a later retry or an earlier day's leftover isn't counted. Only
 * fetches once today's batch row exists. RLS scopes sms_messages to the
 * caller's store; read-only.
 */
export const QUEUED_POLL_MS = 30 * 1000;

export const useQueuedInviteCount =(campaignId: string | null) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<number>({
    queryKey: ["sms-invite-queued-count", campaignId],
    enabled: !!campaignId,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { count, error } = await sb
        .from("sms_messages")
        .select("id", { count: "exact", head: true })
        .eq("campaign_id", campaignId)
        .eq("kind", "invite")
        .eq("direction", "outbound")
        .eq("status", "queued")
        .lte("not_before", new Date().toISOString());
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => ((query.state.data ?? 0) > 0 ? QUEUED_POLL_MS : false),
  });
};
